import { Icon } from '@iconify/react'
import { useLegalDocumentAdmin } from '@/hooks/useLegalDocumentAdmin'
import type { LegalDocumentType } from '@/types/legal-documents'
import styles from './LegalDocumentsPage.module.css'

interface LegalDocumentTabPageProps {
  documentType: LegalDocumentType
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString('pt-BR')
}

export function LegalDocumentTabPage({ documentType }: LegalDocumentTabPageProps) {
  const {
    data,
    version,
    setVersion,
    content,
    setContent,
    isLoading,
    isPublishing,
    error,
    successMessage,
    selectVersion,
    publish,
  } = useLegalDocumentAdmin(documentType)

  if (isLoading) {
    return <p className={styles.feedback}>Carregando documentos...</p>
  }

  if (!data) {
    return (
      <p className={styles.error} role="alert">
        {error ?? 'Não foi possível carregar os documentos.'}
      </p>
    )
  }

  return (
    <div className={styles.tabPage}>
      <section className={styles.panel}>
        <h2 className={styles.panelTitle}>Versão em vigor</h2>
        {data.current ? (
          <div className={styles.currentCard}>
            <div className={styles.currentMeta}>
              <span className={styles.versionBadge}>v{data.current.version}</span>
              <span className={styles.metaText}>
                Publicada em {formatDateTime(data.current.published_at)}
              </span>
            </div>
            <p className={styles.preview}>
              {data.current.content.slice(0, 280)}
              {data.current.content.length > 280 ? '…' : ''}
            </p>
          </div>
        ) : (
          <p className={styles.feedback}>Nenhuma versão publicada para este documento.</p>
        )}
      </section>

      <section className={styles.panel}>
        <h2 className={styles.panelTitle}>Publicar nova versão</h2>
        <p className={styles.panelHint}>
          Ao publicar, a nova versão passa a ser obrigatória para instituições e colaboradores.
          A versão é preenchida com a data de hoje (DD/MM/AAAA); você pode alterá-la antes de publicar.
        </p>

        <div className={styles.field}>
          <label className={styles.label} htmlFor={`legal-version-${documentType}`}>
            Versão
          </label>
          <input
            id={`legal-version-${documentType}`}
            className={styles.input}
            value={version}
            onChange={(event) => setVersion(event.target.value)}
            placeholder="Ex.: 19/05/2026"
            autoComplete="off"
          />
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor={`legal-content-${documentType}`}>
            Conteúdo
          </label>
          <textarea
            id={`legal-content-${documentType}`}
            className={styles.textarea}
            value={content}
            onChange={(event) => setContent(event.target.value)}
            rows={18}
            spellCheck={true}
          />
        </div>

        {error && (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}
        {successMessage && (
          <p className={styles.success} role="status">
            {successMessage}
          </p>
        )}

        <button
          type="button"
          className={styles.publishButton}
          disabled={isPublishing}
          onClick={() => void publish()}
        >
          <Icon icon="mdi:publish" />
          {isPublishing ? 'Publicando...' : 'Publicar nova versão'}
        </button>
      </section>

      <section className={styles.panel}>
        <h2 className={styles.panelTitle}>Histórico de versões</h2>
        {data.versions.length === 0 ? (
          <p className={styles.feedback}>Nenhuma versão registrada.</p>
        ) : (
          <ul className={styles.versionList}>
            {data.versions.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  className={styles.versionItem}
                  onClick={() => selectVersion(item)}
                >
                  <span className={styles.versionItemTitle}>
                    v{item.version}
                    {item.is_current && (
                      <span className={styles.currentTag}>Atual</span>
                    )}
                  </span>
                  <span className={styles.metaText}>
                    {formatDateTime(item.published_at)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}
